import { useAtomValue } from 'jotai';

import { ListViewGroup, ListViewRow, Stack, Text } from '@ekhodealer/ekho-common/rev';

import { AccordionSection } from '@common/components/AccordionSection';
import { InfoLineItem } from '@common/components/InfoLineItem';
import { PricedLineItem } from '@common/components/PricedLineItem';
import { formatCurrency } from '@common/lib/currency';

import { useGetPreorderDetails } from '../../api/useGetPreorderDetails';
import { preorderDetailsDrawerAtom } from '../../atoms/preorderDetailsDrawerAtoms';

const SECTION_TITLE = 'Vehicle details';

export const VehicleSection = (): React.ReactNode => {
	const { preorderId } = useAtomValue(preorderDetailsDrawerAtom);
	const { data: preorderDetails, isLoading } = useGetPreorderDetails(
		preorderId ?? undefined,
	);

	if (isLoading) {
		return (
			<AccordionSection title={SECTION_TITLE} defaultExpanded>
				<Stack itemsSpacing='16'>
					<InfoLineItem title='Vehicle' isLoading />
					<InfoLineItem title='Color' isLoading />
					<InfoLineItem title='Base price' isLoading />
				</Stack>
			</AccordionSection>
		);
	}

	if (!preorderDetails) {
		return null;
	}

	const { vehicle } = preorderDetails;

	return (
		<AccordionSection title={SECTION_TITLE} defaultExpanded>
			<Stack itemsSpacing='16'>
				<ListViewGroup>
					<ListViewRow
						bottomContent={
							<PricedLineItem
								imageUrl={vehicle.imgUrl}
								primaryLabel={vehicle.name}
								secondaryLabel={vehicle.color}
								price={vehicle.basePrice}
							/>
						}
					/>
				</ListViewGroup>
				{vehicle.specs.length > 0 && (
					<>
						<Text size='footnote' weight='semibold'>
							Specs
						</Text>
						<ListViewGroup>
							{vehicle.specs.map((spec) => (
								<ListViewRow
									key={`${spec.name}-${spec.option}`}
									bottomContent={
										<PricedLineItem
											primaryLabel={spec.name}
											secondaryLabel={spec.option}
											price={spec.price}
										/>
									}
								/>
							))}
						</ListViewGroup>
					</>
				)}
				{vehicle.basePriceWithSpecs != null &&
					vehicle.basePriceWithSpecs !== vehicle.basePrice && (
						<InfoLineItem
							title='Base price with specs'
							value={formatCurrency(vehicle.basePriceWithSpecs)}
						/>
					)}
				{vehicle.sku && <InfoLineItem title='SKU' value={vehicle.sku} isCopyable />}
			</Stack>
		</AccordionSection>
	);
};
